import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { getUsageLog, UsageLogInfo } from 'api/usageLog'
import FilterLogDialog from 'components/AppDialogs/FilterLogDialog'
import ReaderHelpIcon from 'components/ReaderHelpIcon'
import FullHeightFlexContainer from 'components/ui/FullHeightFlexContainer'
import { startOfDay, subWeeks } from 'date-fns'
import { useEffect, useState, useCallback } from 'react'

import LogControlArea from './UsageLogControlArea'
import UsageLogList from './UsageLogList'

export type FilterKey = 'action' | 'document_name' | 'status' | 'message' | 'user_name'

export type FilterObjectState = Record<FilterKey, string>

const initFilterObjectState = {
  action: '',
  document_name: '', // eslint-disable-line @typescript-eslint/naming-convention
  status: '',
  message: '',
  user_name: '', // eslint-disable-line @typescript-eslint/naming-convention
} as FilterObjectState

export default function UsageLog() {
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [fromDate, setFromDate] = useState<Date>(subWeeks(startOfDay(new Date()), 1))
  const [toDate, setToDate] = useState<Date>(new Date())
  const [openFilterDialog, setOpenFilterDialog] = useState(false)
  const [filterObject, setFilterObject] = useState<FilterObjectState>(initFilterObjectState)
  const [usageLog, setUsageLog] = useState<UsageLogInfo[] | null>(null)

  const reload = useCallback(async (params: Record<string, string | number>) => {
    setUsageLog(null) // show loading wrapper
    const { logs, total } = await getUsageLog(params)
    setUsageLog(logs || [])
    setTotal(total)
  }, [])

  useEffect(() => {
    if (
      !fromDate ||
      !toDate ||
      isNaN(fromDate.getTime()) ||
      isNaN(toDate.getTime()) ||
      fromDate.getTime() > toDate.getTime()
    )
      return
    const params = {
      count: perPage,
      page: page,
      fromTime: fromDate.toISOString(),
      toTime: toDate.toISOString(),
    } as Record<string, string | number>
    for (const k in filterObject) {
      params[k] = filterObject[k] || undefined // remove
    }
    reload(params)
  }, [reload, fromDate, toDate, page, perPage, filterObject])

  const handlePageChange = useCallback(async (_e, newPage: number) => {
    setPage(newPage)
  }, [])

  const handlePerPageChange = useCallback(async (e) => {
    const newPerPage = parseInt((e.target as HTMLInputElement).value, 10)
    setPerPage(newPerPage)
  }, [])
  const changeFromDate = useCallback(async (newFromdate: Date) => {
    setPage(0)
    setFromDate(newFromdate)
  }, [])
  const changeToDate = useCallback(async (newTodate: Date) => {
    setPage(0)
    setToDate(newTodate)
  }, [])

  const setFilterLog = useCallback(async (newFilterObject: FilterObjectState) => {
    setPage(0)
    setFilterObject(newFilterObject)
    setOpenFilterDialog(false)
  }, [])

  return (
    <FullHeightFlexContainer>
      <Stack direction="row" alignItems="center">
        <h2>利用ログ</h2>
        <ReaderHelpIcon
          builderPath={'/builder/ja-JP/ocr-reader-doc/check-usage.html#Log'}
          concurPath={'/concur/ja-JP/ocr-reader-doc/check-usage.html#Log'}
          invoicePath={'/invoice/ja-JP/ocr-reader-doc/check-usage.html#Log'}
          orderFormPath={'/order-form/ja-JP/ocr-reader-doc/check-usage.html#Log'}
        />
      </Stack>
      <LogControlArea
        fromDate={fromDate}
        toDate={toDate}
        changeFromDate={changeFromDate}
        changeToDate={changeToDate}
        setOpenDialog={setOpenFilterDialog}
      />
      <Box display="flex" flexWrap="nowrap" overflow="hidden">
        <Box flexGrow={4} minWidth={0}>
          <UsageLogList
            logList={usageLog}
            total={total}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePerPageChange}
          />
        </Box>
      </Box>
      <FilterLogDialog
        open={openFilterDialog}
        setOpenDialog={setOpenFilterDialog}
        filterObject={filterObject}
        setFilterLog={setFilterLog}
      />
    </FullHeightFlexContainer>
  )
}
